import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as QRCode from 'qrcode';
import { User } from '../../_models/user';
import { KtidentityMfaChallenge, KtidentityService } from '../../_services/ktidentity.service';

// Emissor fixo: outro nome cria uma segunda entrada no aplicativo autenticador (guia do Identity, §3)
const TOTP_ISSUER = 'KTGroup';

/**
 * Modal do MFA do KTIdentity, em dois modos:
 * - validação: só o código de 6 dígitos;
 * - cadastro (primeiro acesso): QR Code + chave para digitar + o mesmo campo do código.
 * O segredo TOTP vive só no @Input challenge; o componente pai descarta o desafio ao fechar.
 */
@Component({
  selector: 'app-ktidentity-mfa',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './ktidentity-mfa.component.html',
  styleUrls: ['./ktidentity-mfa.component.scss']
})
export class KtidentityMfaComponent implements OnChanges, AfterViewInit {
  @Input() challenge!: KtidentityMfaChallenge;
  @Input() email = '';
  /** login concluído: usuário com a sessão já gravada */
  @Output() completed = new EventEmitter<User>();
  /** erro que não é "código inválido": o pai fecha o modal e mostra o erro no formulário */
  @Output() failed = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild('codeInput') codeInput?: ElementRef<HTMLInputElement>;

  code = '';
  qrDataUrl = '';
  invalidCode = false;
  copied = false;
  submitting = false;

  constructor(
    public translate: TranslateService,
    private ktidentityService: KtidentityService
  ) {}

  get enrollment(): boolean {
    return !!this.challenge?.mfa_enrollment && !!this.challenge?.mfa_secret;
  }

  get otpauthUrl(): string {
    return `otpauth://totp/${TOTP_ISSUER}?secret=${encodeURIComponent(this.challenge.mfa_secret || '')}&issuer=${TOTP_ISSUER}`;
  }

  ngOnChanges(): void {
    this.code = '';
    this.invalidCode = false;
    this.copied = false;
    this.qrDataUrl = '';
    if (this.enrollment) {
      // QR gerado no navegador, nunca por serviço de terceiros
      QRCode.toDataURL(this.otpauthUrl, { width: 160, margin: 1 })
        .then(url => this.qrDataUrl = url)
        .catch(() => this.qrDataUrl = '');
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.codeInput?.nativeElement.focus());
  }

  onCodeChange(value: string): void {
    // aceita colar com espaços ou traços; envia sozinho no 6º dígito
    this.code = (value || '').replace(/\D/g, '').slice(0, 6);
    if (this.codeInput && this.codeInput.nativeElement.value !== this.code) {
      this.codeInput.nativeElement.value = this.code;
    }
    this.invalidCode = false;
    if (this.code.length === 6) {
      this.submit();
    }
  }

  submit(): void {
    if (this.submitting || this.code.length !== 6) {
      return;
    }
    this.submitting = true;
    this.ktidentityService.validateMfa(this.email, this.challenge, this.code).subscribe(result => {
        this.submitting = false;
        if (result.kind === 'session') {
          this.completed.emit(result.user);
        } else {
          this.failed.emit('invalid_response');
        }
      },
      error => {
        this.submitting = false;
        const code = error?.error?.code;
        if (code === 'mfa_invalid') {
          this.invalidCode = true;
          this.code = '';
          this.codeInput?.nativeElement.focus();
        } else {
          this.failed.emit(code || 'unknown');
        }
      }
    );
  }

  copySecret(): void {
    const secret = this.challenge?.mfa_secret;
    if (!secret || !navigator.clipboard) {
      return;
    }
    navigator.clipboard.writeText(secret).then(() => this.copied = true).catch(() => this.copied = false);
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
